import { emptyFragment } from '@execonline-inc/execonline-ui';
import { useConst } from '@execonline-inc/react-hooks.private';
import { reaction } from 'mobx';
import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import Title from '../../../Title';
import { Footer } from '../../../components/Footer';
import Loading from '../../../components/Loading';
import { intercomContextStore } from '../../../IntercomContextStore';
import { discoveryStore } from '../../../DiscoveryStore';
import useDiscoveryStore from '../../../DiscoveryStore/UseDiscoveryStore';
import { ExperienceContextProvider } from '../Common/Experience/ExperienceContext';
import { Experience } from '../Common/Experience/Types';
import DiscoveryContext, { FromState, learnMore } from '../DiscoveryContext';
import EnrollmentStore from '../DiscoveryPortal/Enrollment/Store';
import ExperiencesStore from '../DiscoveryPortal/ExperienceSelection/Experiences/ExperiencesStore';
import { PreviewModeContext } from '../DiscoveryPortal/PreviewMode/PreviewModeContext';
import { RegisterResource } from '../DiscoveryPortal/RegisterResourceStore/Types';
import DeliveryChannelSection from './DeliveryChannelSection';
import ExperienceDetailViewStore from './ExperienceDetailViewStore';
import ExperienceDetailViewReactions from './ExperienceDetailViewStore/ExperienceDetailViewReactions';
import ExperienceDetails from './ExperienceDetails';
import ExperienceHeader from './ExperienceHeader';
import ExperienceHero from './ExperienceHero';

interface Props {
  experienceId: string;
  invitationUuid: string;
  registerResource: RegisterResource;
  enrollmentStore: EnrollmentStore;
  experiencesStore: ExperiencesStore;
  discoveryReferenceState: FromState;
}

// Helper function to extract all unique program IDs from experience
function getProgramIds(experience: Experience): number[] {
  const programIdSet = new Set<number>();

  // Check if experience itself has a programId
  if ('programId' in experience) {
    programIdSet.add(experience.programId);
  }

  // Collect program IDs from all availabilities
  experience.availabilities.forEach((availabilityResource) => {
    programIdSet.add(availabilityResource.payload.programId);
  });

  return Array.from(programIdSet).sort((a, b) => a - b);
}

// Helper function to format program info for Intercom
function formatProgramInfo(experience: Experience): string {
  // Return empty string if essential data is missing
  if (!experience.id || !experience.title?.text) {
    return '';
  }

  const programFamilyId = experience.id;
  const programFamilyName = experience.title.text;
  const programIds = getProgramIds(experience);

  // Format: {id}::{name}::p{id1}::p{id2}::p{id3}
  const parts = [programFamilyId, programFamilyName, ...programIds.map((id) => `p${id}`)];

  return parts.join('::');
}

function ExperienceDetailView({
  experienceId,
  enrollmentStore,
  registerResource,
  experiencesStore,
  invitationUuid,
  discoveryReferenceState,
}: Props) {
  const experienceDetailViewStore = useConst(() => new ExperienceDetailViewStore());
  const [loadingMessage, setLoadingMessage] = useState('');
  useDiscoveryStore();

  useEffect(() => {
    experienceDetailViewStore.loading(experienceId, registerResource);
    // Set loading message after a brief delay to ensure live region is in DOM
    const timer = setTimeout(() => setLoadingMessage('Loading program details...'), 100);
    return () => clearTimeout(timer);
  }, [experienceId, registerResource]);

  useEffect(() => {
    // Update loading message when content loads
    if (experienceDetailViewStore.experienceResource.isJust()) {
      setLoadingMessage('Program details loaded');
    }
  }, [experienceDetailViewStore.experienceResource]);

  useEffect(() => {
    // Use MobX reaction to watch for store changes
    // This avoids putting MobX observables in React dependency arrays
    const dispose = reaction(
      () => ({
        isLoaded: discoveryStore.isLoaded,
        collections: discoveryStore.learningCollections,
        experienceResource: experienceDetailViewStore.experienceResource,
      }),
      ({ isLoaded, collections, experienceResource }) => {
        if (!isLoaded) {
          return;
        }

        // Set the collection context from invitation UUID
        const collection = collections.find(
          (collection) => collection.payload.uuid === invitationUuid,
        );

        if (collection) {
          intercomContextStore.setCurrentCollection(
            collection.payload.id.toString(),
            collection.payload.name.text,
          );
        }

        // Set the program context with program family info when experience is loaded
        experienceResource
          .map((experienceResource) => {
            const programInfo = formatProgramInfo(experienceResource.payload);
            intercomContextStore.setCurrentProgram(programInfo);
          })
          .getOrElse(() => {
            intercomContextStore.setCurrentProgram('');
          });
      },
      { fireImmediately: true },
    );

    // Cleanup on unmount
    return () => {
      dispose();
      intercomContextStore.clearCurrentProgram();
      intercomContextStore.clearCurrentCollection();
      intercomContextStore.cleanup();
    };
  }, [invitationUuid, experienceId]);

  return (
    <DiscoveryContext.Provider value={learnMore(discoveryReferenceState)}>
      <div className="flex flex-grow flex-col">
        {/* Visually hidden live region for status announcements */}
        <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
          {loadingMessage}
        </div>
        {experienceDetailViewStore.experienceResource
          .map((experienceResource) => (
            <ExperienceContextProvider value={{ invitationUuid, experienceResource }}>
              <Title title={experienceResource.payload.title.text} />
              <ExperienceHeader />
              <div className="flex-grow">
                <DeliveryChannelSection />
                <ExperienceHero
                  experiencesStore={experiencesStore}
                  enrollmentStore={enrollmentStore}
                  registerResource={registerResource}
                />
                <ExperienceDetails />
              </div>
              <PreviewModeContext.Consumer>
                {(previewMode) => {
                  switch (previewMode) {
                    case 'default':
                      return <Footer />;
                    case 'preview':
                      return emptyFragment();
                  }
                }}
              </PreviewModeContext.Consumer>
            </ExperienceContextProvider>
          ))
          .getOrElse(() => (
            <Loading />
          ))}
        <ExperienceDetailViewReactions
          store={experienceDetailViewStore}
          discoveryPortal={experiencesStore.discoveryPortal}
        />
      </div>
    </DiscoveryContext.Provider>
  );
}

export default observer(ExperienceDetailView);
